import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
// @mui
import { Box } from '@mui/material';

import { SUPPORTED_LANGUAGES } from '../i18n';
import { SITE_URL, languageUrl, ogImageUrl } from '../i18n/urls';

// ----------------------------------------------------------------------

const Page = forwardRef(({ children, meta, ...other }, ref) => {
  const { t, i18n } = useTranslation();

  const lng = i18n.resolvedLanguage;
  const title = `${t('seo.title')} | ${t('seo.siteName')}`;
  const description = t('seo.description');
  const canonical = languageUrl(lng);
  const image = ogImageUrl();

  return (
    <>
      <Helmet htmlAttributes={{ lang: t('seo.htmlLang') }}>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />

        {/* hreflang: one entry per language, plus x-default on the
            language-detecting root so crawlers know where to send everyone else. */}
        {SUPPORTED_LANGUAGES.map(({ code }) => (
          <link key={code} rel="alternate" hrefLang={code} href={languageUrl(code)} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={t('seo.siteName')} />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={t('seo.ogLocale')} />
        {SUPPORTED_LANGUAGES.filter(({ code }) => code !== lng).map(({ code }) => (
          <meta
            key={code}
            property="og:locale:alternate"
            content={i18n.getFixedT(code)('seo.ogLocale')}
          />
        ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />

        {meta}
      </Helmet>

      <Box ref={ref} {...other}>
        {children}
      </Box>
    </>
  );
});

Page.propTypes = {
  children: PropTypes.node.isRequired,
  meta: PropTypes.node,
};

export default Page;
